/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * TurnstileGate — safety-net wrapper around <Turnstile>.
 *
 * The raw widget can silently fail (script blocked by an ad blocker,
 * flaky mobile connection, Cloudflare outage). Without a fallback the
 * user just sees a greyed-out Sign-In button and doesn't know why. This
 * wrapper adds:
 *   - An 8-second load watchdog. If the widget hasn't emitted a token
 *     or an error by then, we surface a red "Security check couldn't
 *     load — please refresh." message with a Retry button that remounts.
 *   - An explicit `status` reported upward via `onStatus`: 'loading',
 *     'ready', 'error'. Consumer pages use it to compute submit-disabled
 *     alongside token presence.
 *
 * Consumers just replace <Turnstile onVerify={...}> with
 * <TurnstileGate onToken={...} onStatus={...} />. All Turnstile props
 * (theme, size, action) pass through.
 */

import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import Turnstile from './Turnstile';

// Watchdog covers ONLY "the Cloudflare script never loaded / widget never
// rendered" (ad blocker, network). Once the widget is on the page it is
// never torn down by this timer: Cloudflare's managed challenge can take
// well over 8s (or wait for a click), and it reports failure through
// error/timeout callbacks itself. (Fixed 2026-10-15: the old 8s timer
// removed a still-running challenge, so admin login could never pass.)
const LOAD_TIMEOUT_MS = 15000;

const TurnstileGate = forwardRef(function TurnstileGate(
  { onToken, onStatus, theme, size, action },
  ref
) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [mountKey, setMountKey] = useState(0);     // bump to force remount on Retry
  const timerRef = useRef(null);
  const innerRef = useRef(null);

  // Emit status upward on every change so consumers can gate their submit.
  useEffect(() => {
    if (onStatus) onStatus(status);
  }, [status, onStatus]);

  // Arm the 8s watchdog on every mount / retry.
  useEffect(() => {
    setStatus('loading');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      console.warn('[TurnstileGate] widget script did not load within 15s — showing error');
      setStatus('error');
    }, LOAD_TIMEOUT_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [mountKey]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (innerRef.current && typeof innerRef.current.reset === 'function') {
        innerRef.current.reset();
      }
    },
    retry() {
      setMountKey((k) => k + 1);
    }
  }), []);

  const handleRendered = () => {
    // Widget is on the page: stop the load watchdog. The challenge may
    // still take a while; Cloudflare's own callbacks decide the outcome.
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const handleVerify = (token) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setStatus('ready');
    if (onToken) onToken(token);
  };
  const handleExpire = () => {
    if (onToken) onToken(null);
    // Widget will auto-refresh a new token; don't flip to error here.
  };
  const handleError = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setStatus('error');
    if (onToken) onToken(null);
  };

  if (status === 'error') {
    return (
      <div style={{ margin: '12px 0' }}>
        <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 6 }}>
          Security check couldn't load — please refresh.
        </p>
        <button
          type="button"
          onClick={() => setMountKey((k) => k + 1)}
          style={{
            fontSize: 12, padding: '4px 10px', borderRadius: 6,
            border: '1px solid #b91c1c', background: '#fff', color: '#b91c1c',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ margin: '12px 0' }}>
      {status === 'loading' && (
        <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 6 }}>
          Loading security check…
        </p>
      )}
      <Turnstile
        key={mountKey}
        ref={innerRef}
        onRendered={handleRendered}
        onVerify={handleVerify}
        onExpire={handleExpire}
        onError={handleError}
        theme={theme}
        size={size}
        action={action}
      />
    </div>
  );
});

export default TurnstileGate;
